import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Icon } from '../components/ui/Icon';
import { MarcaLateral } from '../components/marca/MarcaLateral';
import { useSesionStore } from '../stores/sesionStore';
import { useAvisoDeAlarmas } from '../hooks/useAvisoDeAlarmas';
import { buscarEntrada, NAVEGACION } from './navegacion';
import { useNovedadesBitacora } from '../hooks/useNovedadesBitacora';
import { useOcultarDemo } from '../hooks/useOcultarDemo';
import { env } from '../../config/env';
import estilos from './AppShell.module.css';

/**
 * Armazón de la aplicación: barra lateral, barra superior en móvil y contenido.
 *
 * La barra lateral sólo muestra las secciones que el usuario puede abrir, y
 * por debajo de 1024 px se convierte en un cajón deslizante.
 */
export function AppShell() {
  const perfil = useSesionStore((s) => s.perfil);
  const politica = useSesionStore((s) => s.politica);
  const salir = useSesionStore((s) => s.salir);

  // El aviso vive aquí, no en la pantalla de alarmas: sirve justo cuando no
  // se está mirando esa pantalla.
  useAvisoDeAlarmas();

  // Punto en el menú cuando hay avisos que no se pudieron entregar.
  const hayNovedades = useNovedadesBitacora();

  // Apartar de la vista el cliente de muestra; sólo para el superadministrador.
  useOcultarDemo();

  const ubicacion = useLocation();
  const [cajonAbierto, setCajonAbierto] = useState(false);

  // Al cambiar de sección el cajón debe cerrarse: si no, tapa el contenido
  // que el usuario acaba de pedir.
  useEffect(() => {
    setCajonAbierto(false);
  }, [ubicacion.pathname]);

  // Escape cierra el cajón, como cualquier capa superpuesta.
  useEffect(() => {
    if (!cajonAbierto) return;

    const alPulsarTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCajonAbierto(false);
    };
    window.addEventListener('keydown', alPulsarTecla);
    return () => window.removeEventListener('keydown', alPulsarTecla);
  }, [cajonAbierto]);

  // Con el cajón abierto la página de fondo no debe desplazarse.
  useEffect(() => {
    if (!cajonAbierto) return;

    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = anterior;
    };
  }, [cajonAbierto]);

  if (!perfil || !politica) return null;

  const entradaActual = buscarEntrada(ubicacion.pathname);
  const grupos = NAVEGACION.map((grupo) => ({
    ...grupo,
    entradas: grupo.entradas.filter((e) => politica.puede(e.capacidad)),
  })).filter((grupo) => grupo.entradas.length > 0);

  return (
    <div className={estilos.armazon}>
      <header className={estilos.barraMovil}>
        <button
          type="button"
          className={estilos.botonMenu}
          onClick={() => setCajonAbierto((v) => !v)}
          aria-label={cajonAbierto ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={cajonAbierto}
        >
          <Icon nombre={cajonAbierto ? 'cerrar' : 'menu'} tamano={22} />
        </button>
        <span className={estilos.tituloMovil}>
          {entradaActual?.etiqueta ?? env.marca.nombreApp}
        </span>
      </header>

      {cajonAbierto && (
        <button
          type="button"
          className={estilos.velo}
          onClick={() => setCajonAbierto(false)}
          aria-label="Cerrar menú"
        />
      )}

      <aside
        className={[estilos.lateral, cajonAbierto ? estilos.lateralAbierta : '']
          .filter(Boolean)
          .join(' ')}
        aria-label="Menú principal"
      >
        <div className={estilos.lateralEncabezado}>
          <MarcaLateral />
        </div>

        <nav className={estilos.navegacion}>
          {grupos.map((grupo) => (
            <div key={grupo.titulo}>
              <div className={estilos.tituloGrupo}>{grupo.titulo}</div>
              {grupo.entradas.map((entrada) => (
                <NavLink
                  key={entrada.ruta}
                  to={entrada.ruta}
                  end={entrada.ruta === '/'}
                  title={entrada.descripcion}
                  className={({ isActive }) =>
                    [estilos.enlace, isActive ? estilos.enlaceActivo : ''].filter(Boolean).join(' ')
                  }
                >
                  <Icon nombre={entrada.icono} tamano={20} />
                  {entrada.etiqueta}
                  {entrada.ruta === '/bitacora' && hayNovedades && (
                    <span
                      className={estilos.puntoNovedad}
                      title="Hay avisos que no se pudieron entregar"
                      aria-label="Hay avisos sin entregar"
                    />
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className={estilos.lateralPie}>
          <div className={estilos.datosUsuario}>
            <span className={estilos.nombreUsuario} title={perfil.email ?? undefined}>
              {perfil.email ?? perfil.nombre}
            </span>
            <span className={estilos.rolUsuario}>{perfil.rol.etiqueta}</span>
          </div>

          <div className={estilos.accionesPie}>
            <button type="button" className={estilos.botonSalir} onClick={() => void salir()}>
              <Icon nombre="salir" tamano={16} />
              Cerrar sesión
            </button>
          </div>

          <div className={estilos.version}>{`${env.marca.nombreApp} v${__APP_VERSION__}`}</div>
        </div>
      </aside>

      <main className={estilos.principal}>
        <Outlet />
      </main>
    </div>
  );
}
